export const CAUSES = [
  {
    slug: "life-and-family",
    tag: "Life & Family",
    headline: "Life and family, from the first heartbeat onward",
    description:
      "Every life counted precious — from the mother in the waiting room to the widow at the table. What it looks like to protect and welcome both.",
    image: "/figma/cause-family.png",
    imageAlt: "Friends arm in arm at golden hour",
    overview:
      "Life and family aren't political categories on this show — they're the daily material Stephanie lives in. We talk about pro-life advocacy that goes beyond a slogan, about adoption and foster care, about caring for children with special needs, about supporting mothers before and after the delivery room, and about the widows and elders who quietly hold a community together.",
    whyItMatters: [
      "A culture that protects the vulnerable at the beginning of life almost always protects them at the end of it too.",
      "Families are the first classroom, the first church, and the first hospital most people ever experience.",
      "The people doing the hardest work — foster parents, pregnancy-center volunteers, widow-support ministries — rarely get a microphone. This is one place they get one.",
    ],
    challenges: [
      "Post-Roe legislative landscapes vary wildly by state; conviction has to be paired with local knowledge.",
      "Adoption and foster-care pipelines are chronically under-supported financially and emotionally.",
      "Widows and single mothers are often absent from the church calendar's programming.",
    ],
    direction: [
      "Elevate first-person testimony from parents, foster families, and pregnancy-center staff.",
      "Point listeners to specific, vetted local and national partners — one honest ask per season, not a fundraising drumbeat.",
      "Model conversations that treat the woman and the child as inseparable, not as opposing constituencies.",
    ],
    partners: [
      { name: "Colorado Life Initiative", description: "A grassroots effort to protect every life, from conception forward, in the state of Colorado." },
      { name: "Down Syndrome for Life — Michigan", description: "Advocacy and support for families welcoming children with Down syndrome." },
      { name: "The Widows Project", description: "Practical, ongoing care for widows in the local community — presence, not just programs." },
    ],
    related: ["ep-04", "ep-06"],
    keepReading: ["faith-and-culture", "education-and-formation", "constitutional-freedom"],
  },
  {
    slug: "faith-and-culture",
    tag: "Faith & Culture",
    headline: "Faith that speaks — quietly, clearly, in public",
    description:
      "Jesus in every square, without the shouting. How Christians can carry conviction into a loud culture and still make room for a neighbor.",
    image: "/figma/cause-community.png",
    imageAlt: "Friends in warm conversation around a table",
    overview:
      "Culture doesn't shift because someone won an argument online. It shifts one long, honest conversation at a time — around dinner tables, in group texts, on porches, in break rooms. This is the work of everyday witness, and it doesn't need a stage.",
    whyItMatters: [
      "Christians speaking about faith in public builds the vocabulary the next generation will inherit.",
      "The default cultural tone is contempt; a quieter voice cuts through more than another loud one.",
      "How we disagree in public is often more evangelistic than what we're arguing about.",
    ],
    challenges: [
      "Platforms reward outrage and punish nuance — the exact opposite of what a healthy witness looks like.",
      "Many believers have never seen faith spoken about outside a church building without cringing.",
      "'Christian' has become a political adjective for many listeners before it means anything about Jesus.",
    ],
    direction: [
      "Normalize honest faith talk in ordinary settings — no green room, no polish, no pulpit.",
      "Host conversations that model disagreement without contempt.",
      "Point to writers, teachers, and organizations helping believers show up in public spaces well.",
    ],
    partners: [
      { name: "Normalizing God", description: "Encouraging everyday conversations about faith so talking about Jesus becomes a natural part of daily life." },
      { name: "Truth & Liberty", description: "Christians engaging culture through biblical worldview training and civic education." },
    ],
    related: ["ep-03", "ep-02"],
    keepReading: ["life-and-family", "education-and-formation", "constitutional-freedom"],
  },
  {
    slug: "education-and-formation",
    tag: "Education & Formation",
    headline: "Teach the Word and the world together",
    description:
      "Christian formation and civic literacy travel together. Where the next generation learns to love their neighbor and their nation.",
    image: "/figma/cause-education.png",
    imageAlt: "A notebook and pen in warm light",
    overview:
      "Education is the long game — measured in decades, not election cycles. The kids in a first-grade classroom this fall are the voters, the parents, the pastors, and the teachers of 2050. What they're taught about God, about the Constitution, and about their neighbor is the culture we'll inherit.",
    whyItMatters: [
      "Formation happens whether it's intentional or not — the question is only who's doing it.",
      "A generation formed in Scripture and the founding documents makes a country far harder to bully.",
      "Local schools are the first place most parents get to practice faithful citizenship.",
    ],
    challenges: [
      "Curriculum debates get framed as culture-war theater when they're usually about very concrete lesson plans.",
      "Christian schooling and public-school engagement are often pitted against each other — most families need both to work.",
      "Civic literacy has quietly collapsed; many adults couldn't name the three branches of government.",
    ],
    direction: [
      "Feature teachers, principals, and released-time programs doing quiet, effective work.",
      "Point parents to specific resources for both Bible literacy and civic education at home.",
      "Model respectful, well-prepared engagement with local school boards.",
    ],
    partners: [
      { name: "Charis Bible College", description: "Discipleship, teaching, and equipping students to know the Word and take it into the world." },
      { name: "LifeWise Academy", description: "Released-time Bible education for public school students during the school day." },
      { name: "Patriot Academy", description: "Constitutional education and biblical citizenship training for the next generation of leaders." },
      { name: "Leadership Institute", description: "Practical training for public-facing leaders who want to serve well and stand firm." },
    ],
    related: ["ep-08"],
    keepReading: ["life-and-family", "faith-and-culture", "constitutional-freedom"],
  },
  {
    slug: "constitutional-freedom",
    tag: "Constitutional Freedom",
    headline: "Founding truths, still true",
    description:
      "A citizen who knows what the Constitution says — and where it came from — is the citizen a free republic depends on.",
    image: "/figma/cause-community.png",
    imageAlt: "Friends in warm conversation around a table",
    overview:
      "The American experiment isn't self-sustaining — it depends on a citizenry that actually understands the document, the reasoning behind it, and the responsibilities that come with the freedoms it protects. This is neither triumphalism nor nostalgia. It's civic maintenance.",
    whyItMatters: [
      "Freedoms not exercised and not understood tend to atrophy — silently, then all at once.",
      "The Constitution presumes a citizenry with certain habits. Those habits have to be taught.",
      "First-amendment religious liberty in particular protects the freedom of every neighbor, not just believers.",
    ],
    challenges: [
      "Constitutional education is often absent from public curricula and thin in Christian ones.",
      "'Rights talk' has replaced 'responsibilities talk' in most civic conversation.",
      "Threats to religious liberty are often subtle and administrative before they're ever loud.",
    ],
    direction: [
      "Bring on guests who teach the Constitution accurately — including where it's uncomfortable.",
      "Explore how faithful citizenship actually shows up between elections, not only during them.",
      "Interview national-security and civil-liberties educators who help citizens read the moment clearly.",
    ],
    partners: [
      { name: "Patriot Academy", description: "Constitutional education and biblical citizenship training for the next generation of leaders." },
      { name: "Leadership Institute", description: "Practical training for public-facing leaders who want to serve well and stand firm." },
      { name: "John Guandolo", description: "National-security education for citizens who want to understand what they're standing for." },
      { name: "ACT for America — Colorado Springs", description: "Educating neighbors on threats to freedom and equipping them to respond wisely." },
    ],
    related: ["ep-09", "ep-07"],
    keepReading: ["life-and-family", "faith-and-culture", "education-and-formation"],
  },
  {
    slug: "community-engagement",
    tag: "Community Engagement",
    headline: "Faithful citizenship, starting at home",
    description:
      "Faithful citizenship practiced close to home — school boards, city councils, county meetings, and the neighbors right next door.",
    image: "/figma/cause-community.png",
    imageAlt: "Friends in warm conversation around a table",
    overview:
      "The most consequential politics for most families happen 20 minutes from their kitchen table — the school board, the county commission, the zoning meeting no one attends. Faithful presence in these rooms is the quiet work that shapes a place.",
    whyItMatters: [
      "Local decisions touch daily life more directly than most national ones.",
      "Showing up in person changes rooms. Refusing to show up cedes them.",
      "Neighborhood-level trust is the substrate every other kind of trust is built on.",
    ],
    challenges: [
      "Most citizens have never attended a school-board or city-council meeting and don't know how.",
      "Local media is thin; misinformation about local decisions spreads unchallenged.",
      "'Community' has been outsourced to social platforms that can't actually replace a neighbor.",
    ],
    direction: [
      "Feature ordinary citizens who quietly serve on boards, in committees, and at the polling place.",
      "Explain how to read a local agenda, prepare a two-minute public comment, and follow through.",
      "Highlight neighborhood-level ministries doing what algorithms can't.",
    ],
    partners: [
      { name: "Teller County Republicans", description: "Local, hands-on citizenship in Stephanie's home county — showing up, listening, serving." },
      { name: "National School Boards Coalition", description: "Parents and citizens engaging school boards with respect and resolve." },
    ],
    related: ["ep-07", "ep-03"],
    keepReading: ["life-and-family", "faith-and-culture", "education-and-formation"],
  },
  {
    slug: "missions-and-ministry",
    tag: "Missions & Ministry",
    headline: "The Gospel doesn't stop at the county line",
    description:
      "Missions matter — at home and abroad — because every person deserves a chance to hear. Where the sending, staying, and supporting all meet.",
    image: "/figma/cause-missions.jpg",
    imageAlt: "Two hands reaching toward each other",
    overview:
      "Missions is often the last item on a church budget and the first thing to feel abstract from a living room in Colorado. This is where we make it concrete — with the people actually doing the work, and the families sending them.",
    whyItMatters: [
      "The Gospel has always crossed cultures — that is not optional to the story.",
      "Missionary families quietly bear costs the sending church rarely sees.",
      "Global missions is one of the few remaining places the American church still gets to think in decades, not quarters.",
    ],
    challenges: [
      "Missions funding is fragile and often the first thing cut when budgets tighten.",
      "Short-term trips can crowd out long-term partnerships if they aren't stewarded well.",
      "Support for missionary families back home is inconsistent — care packages don't cure loneliness.",
    ],
    direction: [
      "Interview field missionaries about what actually helps and what doesn't.",
      "Highlight organizations doing durable, indigenous-led work.",
      "Make it easy for listeners to find one missionary family they can pray for by name.",
    ],
    partners: [
      { name: "RLM Congo", description: "Discipleship and practical care partnerships across communities in the Democratic Republic of the Congo." },
      { name: "True Mercy Africa", description: "Compassion and Gospel work meeting real needs across the African continent." },
      { name: "Ambassadors to the Nations", description: "Sending everyday believers to serve where the Gospel is still new." },
    ],
    related: ["ep-06"],
    keepReading: ["life-and-family", "faith-and-culture", "education-and-formation"],
  },
];

export const getCause = (slug) => CAUSES.find((c) => c.slug === slug);
